/* eslint-disable no-nested-ternary */
import { InboundProcess } from '@features/supplyChain/types';
import { useReactHookFormWithFieldArray } from '@hooks';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import { Input } from '../../../tailwindComponents/Input/Input';
import { Button } from '../../../tailwindComponents/Button/Button';
import { Datepicker } from '../../../tailwindComponents/Datepicker/Datepicker';
import { validationSchema, makeDefaultValues } from './form';
import { CreatDraftGoodsReceivedNoteFormValues } from './types';
import { Textarea } from '../../../tailwindComponents/Textarea';

interface CreateGoodsReceivedNoteFormProps {
  selectedInboundProcess: InboundProcess;
  onClose: () => void;
  onSubmit: (values: CreatDraftGoodsReceivedNoteFormValues) => void;
  isDesktop: boolean;
  isTablet: boolean;
}

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function StatusIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <polyline points='12 6 12 12 16 14' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <line x1='15' x2='9' y1='9' y2='15' />
      <line x1='9' x2='15' y1='9' y2='15' />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <polyline points='20 6 9 17 4 12' />
    </svg>
  );
}

export function CreateDraftGoodsReceivedNoteForm({
  selectedInboundProcess,
  onClose,
  onSubmit,
  isDesktop,
  isTablet,
}: CreateGoodsReceivedNoteFormProps) {
  const {
    handleSubmit,
    control,
    isSubmitting,
    isValid,
    fields: skusFields,
    formState,
    watch,
  } = useReactHookFormWithFieldArray<CreatDraftGoodsReceivedNoteFormValues>({
    defaultValues: makeDefaultValues(selectedInboundProcess),
    validationSchema,
    fieldArrayName: 'skus',
  });

  if (!selectedInboundProcess) {
    return null;
  }

  const { userBusinessName, status } = selectedInboundProcess;
  const { shipmentNoteNumber, createdAt, estimatedTimeOfArrival } =
    selectedInboundProcess.shipmentNote;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shipmentNoteNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Shipment note #{shipmentNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy shipment note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Client name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Client name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {userBusinessName}
          </span>
        </div>

        {/* Creation date */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Creation date</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(createdAt).format('D MMM YYYY')}
          </span>
        </div>

        {/* Status */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <StatusIcon />
            <span className='text-xs font-medium'>Status</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>{status}</span>
        </div>

        {/* Expected date of arrival */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Expected arrival</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs
              .utc(estimatedTimeOfArrival.dateTime)
              .tz(estimatedTimeOfArrival.timeZone)
              .format('D MMM YYYY')}
          </span>
        </div>
      </div>

      {/* Actual Date of Arrival - Editable Field */}
      <div className='px-6 py-4 border-b border-gray-200'>
        <div className='max-w-xs'>
          <div className='flex items-center gap-1.5 text-gray-500 mb-2'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Actual date of arrival</span>
          </div>
          <Controller
            name='actualDateOfArrival'
            control={control}
            render={({ field }) => <Datepicker {...field} />}
          />
        </div>
      </div>

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>
          SKU / items
        </h3>

        {/* Table Header - Desktop only */}
        {isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_2fr_1fr_1fr_1fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU name
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Despatched
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Delivered
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Discrepancy
            </span>
          </div>
        )}

        {/* SKU Rows */}
        <div className='flex flex-col gap-6'>
          {skusFields.map((skusField, index) => {
            const despatchedQuantity = watch(
              `skus.${index}.despatchedQuantity`
            );
            const receivedSkuQuantity = watch(
              `skus.${index}.receivedSkuQuantity`
            );
            const isDiscrepancy =
              receivedSkuQuantity !== undefined &&
              Number(despatchedQuantity) !== Number(receivedSkuQuantity);

            return (
              <div
                key={skusField.id}
                className={`${
                  index !== skusFields.length - 1
                    ? 'pb-6 border-b border-gray-100'
                    : ''
                }`}
              >
                {isDesktop ? (
                  /* Desktop Layout */
                  <div className='grid grid-cols-[2.5rem_1fr_2fr_1fr_1fr_1fr] gap-4 items-start'>
                    {/* Index */}
                    <div className='flex justify-center pt-2'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* SKU ID */}
                    <div className='text-sm text-gray-900 pt-2'>
                      {skusField.skuIdentifier}
                    </div>
                    {/* SKU name */}
                    <div className='flex flex-col pt-2'>
                      <span className='text-sm text-gray-900'>
                        {skusField.skuName}
                      </span>
                      <span className='text-xs text-gray-500'>
                        {skusField.skuUnitOfMeasure}
                      </span>
                    </div>
                    {/* Despatched quantity */}
                    <div className='text-sm text-gray-900 pt-2'>
                      {skusField.despatchedQuantity}
                    </div>
                    {/* Delivered quantity - editable */}
                    <div>
                      <Controller
                        name={`skus.${index}.receivedSkuQuantity`}
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            type='number'
                            sizeVariant='sm'
                            value={
                              field.value !== undefined
                                ? String(field.value)
                                : ''
                            }
                          />
                        )}
                      />
                    </div>
                    {/* Discrepancy reason */}
                    <div>
                      {isDiscrepancy && (
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => (
                            <Input
                              {...field}
                              placeholder='Reason'
                              sizeVariant='sm'
                              isInvalid={Boolean(error && isTouched)}
                              validationMessage={error?.message}
                              value={field.value ?? ''}
                            />
                          )}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  /* Mobile/Tablet Layout */
                  <div className='flex gap-3'>
                    {/* Index */}
                    <div className='flex-shrink-0'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* Details */}
                    <div
                      className={`flex-1 grid gap-4 ${
                        isTablet ? 'grid-cols-2' : 'grid-cols-1'
                      }`}
                    >
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          SKU ID
                        </span>
                        <span className='text-sm text-gray-900'>
                          {skusField.skuIdentifier}
                        </span>
                      </div>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          SKU name
                        </span>
                        <span className='text-sm text-gray-900'>
                          {skusField.skuName} ({skusField.skuUnitOfMeasure})
                        </span>
                      </div>
                      <div className='flex flex-col gap-0.5'>
                        <span className='text-xs font-medium text-gray-500'>
                          Despatched Qty
                        </span>
                        <span className='text-sm text-gray-900'>
                          {skusField.despatchedQuantity}
                        </span>
                      </div>
                      <div className='flex flex-col gap-1'>
                        <span className='text-xs font-medium text-gray-500'>
                          Delivered Qty
                        </span>
                        <Controller
                          name={`skus.${index}.receivedSkuQuantity`}
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type='number'
                              sizeVariant='sm'
                              value={
                                field.value !== undefined
                                  ? String(field.value)
                                  : ''
                              }
                            />
                          )}
                        />
                      </div>
                      {isDiscrepancy && (
                        <div
                          className={`flex flex-col gap-1 ${
                            isTablet ? 'col-span-2' : ''
                          }`}
                        >
                          <span className='text-xs font-medium text-gray-500'>
                            Discrepancy reason
                          </span>
                          <Controller
                            name={`skus.${index}.discrepancyReason`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                {...field}
                                placeholder='Enter reason'
                                sizeVariant='sm'
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                value={field.value ?? ''}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment Section */}
      <div className='px-6 py-4 border-t border-gray-200'>
        <Controller
          name='comment'
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              label='Comment'
              placeholder='Add any additional comments...'
              optional
            />
          )}
        />
      </div>

      {/* Footer Actions */}
      <div
        className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
          isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
        }`}
      >
        <Button
          label='Discard'
          intent='secondary'
          size='sm'
          onClick={onClose}
          startIcon={<CloseIcon />}
          className={!isDesktop ? 'w-full' : ''}
        />
        <Button
          label={isSubmitting ? 'Submitting...' : 'Confirm'}
          intent='primary'
          size='sm'
          type='submit'
          disabled={!isValid || isSubmitting}
          startIcon={<CheckIcon />}
          className={!isDesktop ? 'w-full' : ''}
        />
      </div>
    </form>
  );
}
